import React, { useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';

import { useGetSuppliers } from '~/hooks/apis/tours/useTourSuppliers';

import { Supplier } from '~/types/tours/supplier';

import SupplierFormDialog from './SupplierFormDialog';

interface Props {
  open: boolean;
  title?: string;
  description?: string;
  onClose: () => void;
  onSelect: (supplier: Supplier) => void;
}

const SEARCH_DEBOUNCE_MS = 300;

export default function SupplierPickerDialog({ open, title, description, onClose, onSelect }: Props) {
  const [inputValue, setInputValue] = useState('');
  const [name, setName] = useState('');
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  useEffect(() => {
    const handle = setTimeout(() => setName(inputValue.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [inputValue]);

  const { data: suppliers = [], isLoading, error } = useGetSuppliers(name);

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>{title ?? 'Choose a supplier'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            {!!description && <DialogContentText>{description}</DialogContentText>}

            <Autocomplete
              value={supplier}
              onChange={(_event, next) => setSupplier(next)}
              inputValue={inputValue}
              onInputChange={(_event, next) => setInputValue(next)}
              options={suppliers}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, selected) => option.id === selected.id}
              loading={isLoading}
              filterOptions={(all) => all}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Supplier"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoading ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {!!error && <Alert severity="error">{error.message}</Alert>}

            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setIsCreateOpen(true)}
              sx={{ alignSelf: 'flex-start' }}
            >
              Create a new supplier
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" disabled={!supplier} onClick={() => onSelect(supplier)}>
            Next
          </Button>
        </DialogActions>
      </Dialog>

      {isCreateOpen && (
        <SupplierFormDialog
          open={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          onCreated={(created) => {
            setSupplier(created);
            setInputValue(created.name);
          }}
        />
      )}
    </>
  );
}
